/* v3-core.js: shared behaviours for the v3 design system. No framework; the only global is window.v3.
   Drawers ([data-v3-drawer] opened by [data-v3-open]), header nav overlays ([data-v3-navmenu]), quantity steppers, predictive search, market banner,
   drop signup ([data-v3-signup]), popup, header shadow, video facades, background clips ([data-v3-autoplay]), social embed facades, <v3-carousel>,
   <v3-rail>, auto submitting forms.
   Shared helpers for the section scripts: v3.debounce, v3.parseFragment, v3.fetchFragment, v3.buildIndex,
   v3.filterField. Loaded from <head> with defer, so it runs before every section script (they are deferred too).
   Cart mutations live in v3-cart.js, product form logic in v3-product.js. */
(function () {
  'use strict';
  var doc = document;
  var v3 = window.v3 = window.v3 || {};
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  v3.root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  v3.reduceMotion = reduceMotion;

  /* ---------- shared helpers --------------------------------------- */
  /** Trailing debounce: the returned function runs fn once, ms after the last call. */
  v3.debounce = function (fn, ms) {
    var t = null;
    var d = function () { var self = this, args = arguments; clearTimeout(t); t = setTimeout(function () { fn.apply(self, args); }, ms); };
    d.cancel = function () { clearTimeout(t); };
    return d;
  };
  /** Parse an HTML string and return the first element matching selector (null when absent). */
  v3.parseFragment = function (html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  };
  /** Fetch a URL as HTML (a section render, the recommendations or suggest endpoints) and pick one element out of it. */
  v3.fetchFragment = function (url, selector) {
    return fetch(url).then(function (r) { return r.text(); }).then(function (html) { return v3.parseFragment(html, selector); });
  };
  /** Build a jump index into container from entries [{ id, label, count }].
      opts.item wraps each link in that tag (e.g. 'li'); opts.linkClass, opts.countClass; opts.onClick(entry, event).
      Returns [{ entry, link, count, item }] for the entries that had an id and a label. */
  v3.buildIndex = function (container, entries, opts) {
    opts = opts || {};
    var frag = doc.createDocumentFragment(), out = [];
    entries.forEach(function (entry) {
      if (!entry.id || !entry.label) return;
      var link = doc.createElement('a'), count = null;
      link.href = '#' + entry.id;
      if (opts.linkClass) link.className = opts.linkClass;
      if (entry.count == null) link.textContent = entry.label;
      else {
        var label = doc.createElement('span'); label.textContent = entry.label;
        count = doc.createElement('span'); if (opts.countClass) count.className = opts.countClass; count.textContent = entry.count;
        link.appendChild(label); link.appendChild(count);
      }
      if (opts.onClick) link.addEventListener('click', function (e) { opts.onClick(entry, e); });
      var item = link;
      if (opts.item) { item = doc.createElement(opts.item); item.appendChild(link); }
      frag.appendChild(item);
      out.push({ entry: entry, link: link, count: count, item: item });
    });
    container.appendChild(frag);
    return out;
  };
  /** Wire a filter field: opts.input (required), opts.form (submit filters without navigating), opts.clear (button),
      opts.apply(value) runs debounced on input (opts.delay, default 120ms), on submit and on reset; Escape with text
      resets. opts.reset() replaces the default reset (clear the input, apply('')). Returns { reset }. */
  v3.filterField = function (opts) {
    var input = opts.input, apply = opts.apply;
    var reset = function () { if (opts.reset) opts.reset(); else { input.value = ''; apply(''); } };
    var run = v3.debounce(function () { apply(input.value); }, opts.delay || 120);
    input.addEventListener('input', run);
    input.addEventListener('keydown', function (e) { if (e.key === 'Escape' && input.value) { e.preventDefault(); run.cancel(); reset(); } });
    if (opts.form) opts.form.addEventListener('submit', function (e) { e.preventDefault(); run.cancel(); apply(input.value); input.blur(); });
    if (opts.clear) opts.clear.addEventListener('click', function () { run.cancel(); reset(); input.focus(); });
    return { reset: reset };
  };

  /* ---------- drawers ------------------------------------------------ */
  var PANEL = '.v3-drawer__panel, .v3-sheet__panel';
  var openDrawer = null, lastFocus = null, closeTimer = null, closing = null;
  function drawerEl(name) { return doc.querySelector('[data-v3-drawer="' + name + '"]'); }
  function isVisible(el) { return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length); }
  function focusables(root) {
    var all = root.querySelectorAll('a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([type="hidden"]):not([tabindex="-1"]), textarea:not([tabindex="-1"]), select:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])');
    return Array.prototype.filter.call(all, isVisible);
  }
  function setExpanded(name, state) {
    var triggers = doc.querySelectorAll('[data-v3-open="' + name + '"]');
    for (var i = 0; i < triggers.length; i++) triggers[i].setAttribute('aria-expanded', state ? 'true' : 'false');
  }
  /* Scroll lock. overflow: hidden on <html> is enough everywhere except iOS Safari, which still scrolls the page
     under a fixed overlay when the visitor swipes the scrim. Pinning the body at its current offset stops that; the
     offset is restored on close so the page does not jump, the sticky header is held in place while pinned, and the
     scrollbar gap is padded so desktop does not shift. */
  var lockedY = null;
  function lockScroll() {
    if (lockedY !== null) return;
    var body = doc.body, gap = window.innerWidth - doc.documentElement.clientWidth;
    var header = doc.querySelector('.v3-header-section'), headerTop = header ? header.getBoundingClientRect().top : 0;
    lockedY = window.scrollY || window.pageYOffset || 0;
    doc.documentElement.classList.add('v3-scroll-lock');
    body.style.position = 'fixed'; body.style.top = -lockedY + 'px'; body.style.left = '0'; body.style.right = '0'; body.style.width = '100%';
    if (gap > 0) body.style.paddingRight = gap + 'px';
    /* the sticky header rides up with the pinned body: hold it where it was on screen */
    if (header) { var shift = headerTop - header.getBoundingClientRect().top; if (shift) header.style.transform = 'translateY(' + shift + 'px)'; }
  }
  function unlockScroll() {
    if (lockedY === null) return;
    var body = doc.body, y = lockedY; lockedY = null;
    doc.documentElement.classList.remove('v3-scroll-lock');
    body.style.position = ''; body.style.top = ''; body.style.left = ''; body.style.right = ''; body.style.width = ''; body.style.paddingRight = '';
    var header = doc.querySelector('.v3-header-section'); if (header) header.style.transform = '';
    var html = doc.documentElement, prev = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto'; window.scrollTo(0, y); html.style.scrollBehavior = prev;
  }
  v3.open = function (name) {
    var el = drawerEl(name); if (!el) return;
    if (openDrawer && openDrawer !== el) v3.close(true);
    /* a drawer still fading out must finish hiding now, or its scrim stays over the page with no class to show it */
    clearTimeout(closeTimer);
    if (closing && closing !== el) closing.hidden = true;
    closing = null;
    lastFocus = doc.activeElement;
    el.hidden = false;
    lockScroll();
    void el.offsetWidth; /* commit the start state before transitioning (rAF is throttled in background tabs) */
    el.classList.add('is-open');
    openDrawer = el;
    setExpanded(name, true);
    var panel = el.querySelector(PANEL);
    var target = panel && (panel.querySelector('[data-v3-autofocus]') || focusables(panel)[0] || panel);
    if (target) target.focus({ preventScroll: true });
    el.dispatchEvent(new CustomEvent('v3:drawer:open', { bubbles: true, detail: { name: name } }));
  };
  v3.close = function (immediate) {
    var el = openDrawer; if (!el) return;
    el.classList.remove('is-open');
    setExpanded(el.getAttribute('data-v3-drawer'), false);
    unlockScroll();
    var done = function () { el.hidden = true; if (closing === el) closing = null; };
    if (immediate || reduceMotion) done(); else { closing = el; closeTimer = setTimeout(done, 380); }
    openDrawer = null;
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  };
  doc.addEventListener('click', function (e) {
    var t = e.target.closest('[data-v3-open]');
    if (t) {
      /* a modified click on a linked trigger (the header trolley) is a request for a new tab: let the browser have it */
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault(); v3.open(t.getAttribute('data-v3-open')); return;
    }
    if (e.target.closest('[data-v3-close]')) { e.preventDefault(); v3.close(); }
  });
  doc.addEventListener('keydown', function (e) {
    if (!openDrawer) return;
    if (e.key === 'Escape') { v3.close(); return; }
    if (e.key !== 'Tab') return;
    var panel = openDrawer.querySelector(PANEL); if (!panel) return;
    var f = focusables(panel); if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && doc.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && doc.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* ---------- quantity steppers ------------------------------------- */
  doc.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-v3-qty-dec], [data-v3-qty-inc]'); if (!btn) return;
    var wrap = btn.closest('[data-v3-qty]'); var input = wrap && wrap.querySelector('input'); if (!input) return;
    var step = Number(input.step) || 1, min = input.min !== '' ? Number(input.min) : 0, max = input.max !== '' ? Number(input.max) : Infinity;
    var val = Number(input.value) || 0;
    val = btn.hasAttribute('data-v3-qty-inc') ? val + step : val - step;
    input.value = Math.min(max, Math.max(min, val));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- predictive search ------------------------------------- */
  var searchIdle = null;
  var suggest = v3.debounce(function (input, target, sectionId, q) {
    var url = v3.root + 'search/suggest?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last&section_id=' + encodeURIComponent(sectionId);
    v3.fetchFragment(url, '[data-v3-predictive-results]').then(function (frag) {
      if (frag && input.value.trim() === q) target.innerHTML = frag.outerHTML;
    }).catch(function () { /* the form still submits to /search */ });
  }, 220);
  doc.addEventListener('input', function (e) {
    var input = e.target.closest('[data-v3-predictive-input]'); if (!input) return;
    var drawer = input.closest('[data-v3-drawer]');
    var target = drawer && drawer.querySelector('[data-v3-predictive-target]'); if (!target) return;
    if (searchIdle === null) searchIdle = target.innerHTML;
    var q = input.value.trim();
    if (q.length < 2) { suggest.cancel(); target.innerHTML = searchIdle; return; }
    suggest(input, target, drawer.getAttribute('data-section-id'), q);
  });
  /* the search input carries data-v3-autofocus, so v3.open focuses it */

  /* ---------- market banner (dismissible, never redirects) ---------- */
  function store(key, val) { try { if (val === undefined) return localStorage.getItem(key); localStorage.setItem(key, val); } catch (err) { return null; } }
  var banner = doc.querySelector('[data-v3-market-banner]');
  if (banner && !store(banner.getAttribute('data-key'))) {
    banner.hidden = false;
    banner.addEventListener('click', function (e) {
      if (e.target.closest('[data-v3-market-dismiss]')) { banner.hidden = true; store(banner.getAttribute('data-key'), '1'); }
    });
  }

  /* ---------- drop signup ("Never miss one", sections/v3-signup) ------------------
     [data-v3-signup-open] opens the modal when the page has one, else the link's href (the footer newsletter) is
     followed. The form posts in the background; the answer is read from the returned page (the same form's
     data-v3-signup-state), because Shopify replies to a customer form with a full page. A bot check page, or a
     network failure, falls back to a normal submit. Done swaps every trigger on the page for its done pill and is
     remembered for the visitor. The coming soon "hear first" form ([data-v3-waitlist]) posts the same way and
     answers in place. */
  function postSignup(form, done, fail) {
    if (!window.fetch || !window.DOMParser) return false;
    form.classList.add('is-busy');
    fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then(function (res) {
        if (/\/challenge/.test(res.url)) { form.submit(); return; }
        return res.text().then(function (html) {
          form.classList.remove('is-busy');
          var page = new DOMParser().parseFromString(html, 'text/html');
          var state = page.querySelector('#' + form.id + ' [data-v3-signup-state]');
          var value = state ? state.getAttribute('data-v3-signup-state') : '';
          if (value === 'done' || /customer_posted=true/.test(res.url)) { done(); return; }
          var msg = page.querySelector('#' + form.id + ' [data-v3-signup-error]');
          fail(msg && msg.textContent.trim());
        });
      })
      .catch(function () { form.submit(); });
    return true;
  }
  var SIGNED = 'v3-signed-up';
  var signup = doc.querySelector('[data-v3-signup]');
  function signupDone(save) {
    if (save) store(SIGNED, '1');
    var label = signup && signup.getAttribute('data-done-pill');
    var opens = doc.querySelectorAll('[data-v3-signup-open]');
    for (var i = 0; i < opens.length; i++) {
      opens[i].hidden = true;
      var pill = opens[i].parentNode.querySelector('[data-v3-signup-done-pill]');
      if (pill) { pill.hidden = false; if (label) { var t = pill.querySelector('span'); if (t) t.textContent = label; } }
    }
    if (signup) {
      var ask = signup.querySelector('[data-v3-signup-ask]'), done = signup.querySelector('[data-v3-signup-done]');
      if (ask) ask.hidden = true; if (done) done.hidden = false;
    }
  }
  if (store(SIGNED)) signupDone(false);
  doc.addEventListener('click', function (e) {
    var t = e.target.closest('[data-v3-signup-open]');
    if (!t || !signup) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault(); v3.open('signup');
  });
  if (signup) {
    var sForm = signup.querySelector('form');
    var sState = signup.querySelector('[data-v3-signup-state]');
    /* a normal post came back to this page: show the result and remember a success. Every customer form on the page
       renders as posted (footer, hear first, this one), so the modal only opens for its own post: the form tag puts
       its id in the action as a hash, and the page comes back with it (a bot check can drop it, hence no hash too) */
    var sent = location.hash.slice(1), mine = sForm && (!sent || sent === sForm.id);
    if (sState && sState.getAttribute('data-v3-signup-state') === 'done') { signupDone(true); if (mine) v3.open('signup'); }
    else if (sState && sState.getAttribute('data-v3-signup-state') === 'error' && mine) v3.open('signup');
    if (sForm) sForm.addEventListener('submit', function (e) {
      var err = signup.querySelector('[data-v3-signup-error]');
      if (!postSignup(sForm, function () {
        signupDone(true);
        var close = signup.querySelector('[data-v3-signup-done] [data-v3-close]'); if (close) close.focus();
      }, function (msg) {
        if (err) { err.textContent = msg || err.getAttribute('data-fallback') || ''; err.hidden = false; }
      })) return;
      e.preventDefault();
    });
  }
  /* delegated, so a hear first block re-rendered in the editor still posts in the background */
  doc.addEventListener('submit', function (e) {
    var w = e.target.closest('[data-v3-waitlist]'); if (!w) return;
    var ask = w.querySelector('[data-v3-waitlist-ask]'), done = w.querySelector('[data-v3-waitlist-done]'), err = w.querySelector('[data-v3-signup-error]');
    if (!ask || !done || !postSignup(e.target, function () {
      ask.hidden = true; if (err) err.hidden = true; done.hidden = false; done.focus();
      signupDone(true);
      var bar = doc.querySelector('v3-sticky-bar'); if (bar && bar.update) bar.update(); /* hides it now, not on the next scroll */
    }, function (msg) {
      if (err) { err.textContent = msg || err.getAttribute('data-fallback') || ''; err.hidden = false; }
    })) return;
    e.preventDefault();
  });

  /* ---------- popup (once per visitor, after a delay) --------------- */
  var popup = doc.querySelector('[data-v3-popup]');
  if (popup && !store(popup.getAttribute('data-key'))) {
    setTimeout(function () {
      if (openDrawer) return;
      v3.open('popup');
      store(popup.getAttribute('data-key'), '1');
    }, (Number(popup.getAttribute('data-delay')) || 8) * 1000);
  }

  /* ---------- facades: video (<template>) and social embeds ---------- */
  doc.addEventListener('click', function (e) {
    var play = e.target.closest('[data-v3-video-play]');
    if (play) {
      e.preventDefault();
      var wrap = play.closest('[data-v3-video]'); var tpl = wrap && wrap.querySelector('template');
      if (tpl) {
        wrap.innerHTML = ''; wrap.appendChild(tpl.content.cloneNode(true));
        var vid = wrap.querySelector('video, iframe');
        if (vid) { vid.focus(); if (vid.play) vid.play().catch(function () {}); }
      }
      return;
    }
    var media = e.target.closest('[data-v3-embed]'); if (!media) return;
    var src = media.getAttribute('data-v3-embed'); if (!src) return;
    e.preventDefault();
    var platform = media.getAttribute('data-platform') || '';
    var f = doc.createElement('iframe');
    f.src = src;
    f.title = (platform ? platform.charAt(0).toUpperCase() + platform.slice(1) : 'Embedded') + ' post';
    f.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen');
    f.setAttribute('allowfullscreen', '');
    f.referrerPolicy = 'strict-origin-when-cross-origin';
    media.innerHTML = ''; media.appendChild(f); media.removeAttribute('data-v3-embed');
    f.focus();
  });

  /* ---------- background clips: [data-v3-autoplay] ----------
     The other shape of snippets/v3-video-facade: a muted, looping, silent <video preload="none"> with no play
     button. One observer at half the box in view turns the preload on, plays the clip and pauses it again when
     it leaves, so nothing downloads for a band the visitor never reaches. Skipped whole under reduced motion
     (the poster is the band) and for a wrapper the responsive swap has hidden, which has no box to intersect
     and is checked again before play. Editor re-renders come back through shopify:section:load. */
  var clipWatcher = null;
  function watchClips(root) {
    if (reduceMotion || !('IntersectionObserver' in window)) return;
    if (!clipWatcher) {
      clipWatcher = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var box = entries[i].target, vid = box.querySelector('video'); if (!vid) continue;
          var shown = box.offsetParent !== null || box.getClientRects().length > 0;
          if (entries[i].isIntersecting && shown) {
            if (vid.preload !== 'auto') vid.preload = 'auto';
            var p = vid.play(); if (p && p.catch) p.catch(function () {});
          } else if (!vid.paused) { vid.pause(); }
        }
      }, { threshold: 0.5 });
    }
    var boxes = (root || doc).querySelectorAll('[data-v3-autoplay]');
    for (var i = 0; i < boxes.length; i++) clipWatcher.observe(boxes[i]);
  }
  watchClips();
  doc.addEventListener('DOMContentLoaded', function () { watchClips(); });
  doc.addEventListener('shopify:section:load', function (e) { watchClips(e.target); });

  /* ---------- <v3-carousel>: slides toggle .is-active; pause, autoplay ----------
     A slide may hold a muted looping clip (.v3-slide__video): only the slide on screen plays, the ones leaving
     pause and rewind, and a hidden tab pauses the lot. Rotation is on its own timer, so a video slide moves on
     with the interval rather than waiting for the clip to end. Under reduced motion nothing plays and the
     poster image stands in. A slide with its own phone clip carries two, each with data-media: only the one
     matching the screen plays, and crossing the breakpoint hands over to the other. */
  class V3Carousel extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.slides = this.querySelectorAll('[data-v3-slide]');
      this.pauseBtn = this.querySelector('[data-v3-pause]');
      this.index = 0;
      var track = this.querySelector('[data-v3-track]'); if (track) track.classList.add('is-ready');
      if (this.slides.length) this.slides[0].classList.add('is-active');
      /* registered before the single slide bail out: one slide with a clip still follows the tab's visibility */
      this.onVisibility = function () { if (doc.hidden) { self.stop(); self.pauseClip(); } else { self.start(); self.playClip(); } };
      doc.addEventListener('visibilitychange', this.onVisibility);
      this.onMedia = function () { self.pauseClips(); self.playClip(); };
      this.mq = window.matchMedia('(max-width: 640px)');
      if (this.mq.addEventListener) this.mq.addEventListener('change', this.onMedia);
      this.playClip();
      if (this.slides.length < 2) { if (this.pauseBtn) this.pauseBtn.hidden = true; return; }
      this.onClick = function (e) { if (e.target.closest('[data-v3-pause]')) self.toggle(); };
      this.onHover = function () { self.hovering = true; self.stop(); };
      this.onLeave = function () { self.hovering = false; self.start(); };
      this.addEventListener('click', this.onClick);
      this.addEventListener('mouseenter', this.onHover); this.addEventListener('mouseleave', this.onLeave);
      this.addEventListener('focusin', this.onHover); this.addEventListener('focusout', this.onLeave);
      this.paused = this.getAttribute('data-autoplay') !== 'true' || reduceMotion;
      this.renderPause();
      this.start();
    }
    disconnectedCallback() {
      this.stop(); this.pauseClip(); doc.removeEventListener('visibilitychange', this.onVisibility);
      if (this.mq && this.mq.removeEventListener) this.mq.removeEventListener('change', this.onMedia);
    }
    start() {
      if (this.paused || this.hovering || this.timer) return;
      var self = this, ms = Number(this.getAttribute('data-interval')) || 7000;
      this.timer = setInterval(function () { self.go(self.index + 1); }, ms);
    }
    stop() { clearInterval(this.timer); this.timer = null; }
    toggle() { this.paused = !this.paused; this.renderPause(); if (this.paused) this.stop(); else this.start(); }
    renderPause() {
      if (!this.pauseBtn) return;
      this.pauseBtn.setAttribute('aria-pressed', this.paused ? 'true' : 'false');
      this.pauseBtn.setAttribute('aria-label', this.paused ? this.pauseBtn.getAttribute('data-label-play') : this.pauseBtn.getAttribute('data-label-pause'));
      var icons = this.pauseBtn.querySelectorAll('[data-icon]');
      for (var i = 0; i < icons.length; i++) icons[i].hidden = icons[i].getAttribute('data-icon') !== (this.paused ? 'play' : 'pause');
    }
    go(i) {
      var n = this.slides.length; this.index = (i + n) % n;
      for (var k = 0; k < n; k++) this.slides[k].classList.toggle('is-active', k === this.index);
      this.pauseClips(); this.playClip();
    }
    /* the slide's clip for this screen: one without data-media plays everywhere */
    clipAt(i) {
      var s = this.slides && this.slides[i]; if (!s) return null;
      var vids = s.querySelectorAll('video');
      for (var k = 0; k < vids.length; k++) {
        var m = vids[k].getAttribute('data-media');
        if (!m || window.matchMedia(m).matches) return vids[k];
      }
      return null;
    }
    /* every clip but the one on screen: paused and back at its first frame, so a clip always restarts with its slide */
    pauseClips() {
      var cur = this.clipAt(this.index), vids = this.querySelectorAll('video');
      for (var k = 0; k < vids.length; k++) {
        if (vids[k] === cur) continue;
        vids[k].pause(); vids[k].currentTime = 0;
      }
    }
    /* the one on screen, keeping its place: a hidden tab or a removed carousel, not a slide change */
    pauseClip() { var vid = this.clipAt(this.index); if (vid) vid.pause(); }
    playClip() {
      if (reduceMotion || doc.hidden) return;
      var vid = this.clipAt(this.index); if (!vid) return;
      var p = vid.play(); if (p && p.catch) p.catch(function () {});
    }
  }
  if (!customElements.get('v3-carousel')) customElements.define('v3-carousel', V3Carousel);

  /* ---------- <v3-rail>: arrow paging for a scroll-snap rail ---------------- */
  class V3Rail extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.track = this.querySelector('[data-v3-rail-track]') || this.firstElementChild;
      var arrows = this.getAttribute('data-arrows') ? doc.getElementById(this.getAttribute('data-arrows')) : this;
      /* optional progress line: [data-v3-rail-progress] inside the arrows box or the element itself */
      this.progress = (arrows || this).querySelector('[data-v3-rail-progress]');
      if (this.progress && this.track) {
        this.onScroll = function () { self.renderProgress(); };
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.renderProgress();
      }
      if (!arrows) return;
      arrows.addEventListener('click', function (e) {
        if (e.target.closest('[data-v3-rail-prev]')) self.page(-1);
        else if (e.target.closest('[data-v3-rail-next]')) self.page(1);
      });
    }
    disconnectedCallback() { if (this.onScroll) window.removeEventListener('resize', this.onScroll); }
    renderProgress() {
      var t = this.track, max = t.scrollWidth - t.clientWidth;
      var visible = t.scrollWidth ? t.clientWidth / t.scrollWidth : 1;
      var start = max > 0 ? (t.scrollLeft / max) * (1 - visible) : 0;
      this.progress.style.setProperty('--progress', Math.round(visible * 1000) / 10 + '%');
      this.progress.style.transform = 'translateX(' + (start * 100 / visible) + '%)';
    }
    page(dir) {
      if (!this.track) return;
      var first = this.track.firstElementChild, second = first && first.nextElementSibling;
      var step = second ? (second.getBoundingClientRect().left - first.getBoundingClientRect().left) : this.track.clientWidth;
      var perView = Number(getComputedStyle(this.track).getPropertyValue('--per-view')) || 1;
      this.track.scrollBy({ left: dir * step * perView, behavior: reduceMotion ? 'auto' : 'smooth' });
      if (this.progress) { var self = this; setTimeout(function () { self.renderProgress(); }, reduceMotion ? 50 : 450); }
    }
  }
  if (!customElements.get('v3-rail')) customElements.define('v3-rail', V3Rail);

  /* ---------- accordions (.v3-acc): animated open and close, one open per group ----------
     Native <details> toggles instantly, so the summary click is intercepted: the body animates its height
     (Web Animations, no layout thrash beyond one measure) and sibling rows in the same parent close first.
     Keyboard toggling goes through the same click event. Reduced motion: instant. (4 Sep 2026 review) */
  function accBody(d) { return d.querySelector('.v3-acc__body'); }
  /* settle() runs once, from onfinish or a timer: animations do not advance in a hidden tab, and the row must
     never be left half open */
  function accRun(b, frames, ms, settle) {
    var done = false, fin = function () { if (done) return; done = true; if (a && a.playState !== 'finished') a.cancel(); settle(); };
    b.style.overflow = 'hidden';
    var a = b.animate(frames, { duration: ms, easing: 'cubic-bezier(.2,.7,.2,1)' });
    a.onfinish = fin; setTimeout(fin, ms + 80);
  }
  /* the body's bottom padding animates with the height: a border-box height of 0 cannot go under its padding,
     so without this the close would stop 18px short and jump on the last frame */
  function accOpen(d) {
    var b = accBody(d); d.open = true; if (!b || reduceMotion || !b.animate) return;
    var h = b.scrollHeight, pad = getComputedStyle(b).paddingBottom;
    accRun(b, [{ height: '0px', paddingBottom: '0px', opacity: 0 }, { height: h + 'px', paddingBottom: pad, opacity: 1 }], 280, function () { b.style.overflow = ''; });
  }
  function accClose(d) {
    var b = accBody(d); if (!b || reduceMotion || !b.animate) { d.open = false; return; }
    var h = b.getBoundingClientRect().height, pad = getComputedStyle(b).paddingBottom;
    accRun(b, [{ height: h + 'px', paddingBottom: pad, opacity: 1 }, { height: '0px', paddingBottom: '0px', opacity: 0 }], 220, function () { d.open = false; b.style.overflow = ''; });
  }
  doc.addEventListener('click', function (e) {
    var sum = e.target.closest('.v3-acc__summary'); if (!sum) return;
    var det = sum.parentElement; if (!det || det.tagName !== 'DETAILS') return;
    e.preventDefault();
    if (det.open) { accClose(det); return; }
    var siblings = det.parentElement ? det.parentElement.querySelectorAll(':scope > details.v3-acc[open]') : [];
    siblings.forEach(function (o) { if (o !== det) accClose(o); });
    accOpen(det);
  });

  /* ---------- auto submitting forms: [data-v3-autosubmit] (the sort select on collection and search) ----------
     One change submits the GET form; without JavaScript the form keeps its Apply button inside <noscript>. A ?view=
     preview parameter on the current URL is carried over, so sorting a previewed v3 listing stays on the v3 template. */
  doc.addEventListener('change', function (e) {
    var form = e.target.closest('form[data-v3-autosubmit]'); if (!form) return;
    var view = new URLSearchParams(window.location.search).get('view');
    if (view && !form.querySelector('[name="view"]')) {
      var hidden = doc.createElement('input'); hidden.type = 'hidden'; hidden.name = 'view'; hidden.value = view; form.appendChild(hidden);
    }
    if (form.requestSubmit) form.requestSubmit(); else form.submit();
  });

  /* ---------- header nav overlays ([data-v3-navmenu]: the "By Occasion" list) ----------
     A <details>, so a click opens it without script. With script, on a hover capable pointer the card opens after
     a short pause and closes when the pointer leaves; a click on the summary of a hover opened card keeps it open
     (the next click closes it). Escape closes and returns focus to the summary, focus or a click elsewhere closes,
     and one card is open at a time. */
  var navMenus = doc.querySelectorAll('[data-v3-navmenu]');
  if (navMenus.length) {
    var hoverable = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    var closeNavMenus = function (except) { navMenus.forEach(function (d) { if (d !== except && d.open) d.open = false; }); };
    navMenus.forEach(function (d) {
      var timer, byHover = false, summary = d.querySelector('summary');
      d.addEventListener('toggle', function () { if (d.open) closeNavMenus(d); else byHover = false; });
      if (hoverable) {
        d.addEventListener('pointerenter', function () { clearTimeout(timer); timer = setTimeout(function () { if (!d.open) { byHover = true; d.open = true; } }, 60); });
        d.addEventListener('pointerleave', function () { clearTimeout(timer); timer = setTimeout(function () { d.open = false; }, 180); });
        if (summary) summary.addEventListener('click', function (e) { if (d.open && byHover) { e.preventDefault(); byHover = false; } });
      }
      d.addEventListener('focusout', function (e) { if (e.relatedTarget && !d.contains(e.relatedTarget)) d.open = false; });
      d.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape' || !d.open) return;
        e.preventDefault(); e.stopPropagation(); d.open = false; if (summary) summary.focus();
      });
    });
    doc.addEventListener('click', function (e) { if (!e.target.closest('[data-v3-navmenu]')) closeNavMenus(); });
  }

  /* ---------- cookie settings ([data-v3-cookie-settings], the footer legal bar) ----------
     Reopens Shopify's consent preferences through window.privacyBanner, which content_for_header loads, possibly
     after this script. The button renders hidden and shows only once that API exists, so it is never dead; checked
     now, again on load, and after an editor re-render of the footer. */
  function cookieApi() { return window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function'; }
  function revealCookieSettings() {
    if (!cookieApi()) return;
    doc.querySelectorAll('[data-v3-cookie-settings]').forEach(function (btn) { btn.hidden = false; });
  }
  revealCookieSettings();
  window.addEventListener('load', revealCookieSettings);
  doc.addEventListener('shopify:section:load', revealCookieSettings);
  doc.addEventListener('click', function (e) {
    if (e.target.closest('[data-v3-cookie-settings]') && cookieApi()) window.privacyBanner.showPreferences();
  });

  /* ---------- header shadow on scroll ------------------------------- */
  var header = doc.querySelector('[data-v3-header]');
  if (header) {
    var onScroll = function () { header.classList.toggle('is-scrolled', window.scrollY > 4); };
    onScroll(); window.addEventListener('scroll', onScroll, { passive: true });
  }
})();
